// src/pages/finance-portal/inventory/PurchaseOrders.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Chip, FormControl, IconButton,
    InputAdornment, InputLabel, MenuItem, Paper, Select, Skeleton, Stack,
    Tab, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
    Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatDate, PO_STATUS_COLORS, statusChip,
} from './components/inventoryFormatters';

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Pending', value: 'PENDING_APPROVAL' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Partially Received', value: 'PARTIALLY_RECEIVED' },
    { label: 'Received', value: 'RECEIVED' },
    { label: 'Closed', value: 'CLOSED' },
];

export default function PurchaseOrders() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [vendorFilter, setVendorFilter] = useState(null);
    const [vendors, setVendors] = useState([]);
    const vendorSearchTimer = useRef(null);
    const searchTimer = useRef(null);

    const fetchPOs = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (vendorFilter?.id) params.vendor_id = vendorFilter.id;
            const res = await axiosInstance.get('/api/finance/inventory/purchase-orders', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, search, statusFilter, vendorFilter]);

    useEffect(() => { fetchPOs(); }, [fetchPOs]);

    const handleSearch = (v) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { setPage(0); setSearch(v); }, 300);
    };

    const handleVendorSearch = (v) => {
        if (vendorSearchTimer.current) clearTimeout(vendorSearchTimer.current);
        vendorSearchTimer.current = setTimeout(() => {
            axiosInstance.get('/api/finance/vendors', { params: { search: v, per_page: 50 } })
                .then((r) => setVendors(r.data?.data || [])).catch(() => {});
        }, 300);
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Purchase Orders
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Create, approve, send, and match POs through receipt and billing
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => navigate('/finance-portal/inventory/purchase-orders/new')}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    New PO
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Tabs value={statusFilter} onChange={(e, v) => { setStatusFilter(v); setPage(0); }}
                    variant="scrollable" scrollButtons="auto"
                    sx={{ borderBottom: '1px solid #e2e8f0',
                          '& .MuiTab-root': { fontFamily: FONT_FAMILY, textTransform: 'none' } }}>
                    {STATUS_TABS.map((t) => <Tab key={t.value} label={t.label} value={t.value} />)}
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField size="small" placeholder="Search PO number, notes, quote…"
                            onChange={(e) => handleSearch(e.target.value)} sx={{ flex: 1, minWidth: 280 }}
                            InputProps={{ startAdornment: (
                                <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            ) }} />
                        <Autocomplete size="small" sx={{ minWidth: 280 }}
                            options={vendors}
                            getOptionLabel={(o) => o.display_name || o.company_name || o.vendor_number || ''}
                            value={vendorFilter}
                            onChange={(e, v) => { setVendorFilter(v); setPage(0); }}
                            onInputChange={(e, v) => handleVendorSearch(v)}
                            renderInput={(params) => <TextField {...params} label="Vendor" />}
                        />
                    </Stack>
                </Box>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>PO #</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Order Date</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Expected</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Total</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Received</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <TableRow key={i}><TableCell colSpan={7}><Skeleton height={32} /></TableCell></TableRow>
                            ))
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                    No purchase orders match the current filters
                                </TableCell>
                            </TableRow>
                        ) : rows.map((p) => (
                            <TableRow key={p.id} hover
                                onClick={() => navigate(`/finance-portal/inventory/purchase-orders/${p.id}`)}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                    {p.po_number}
                                    {p.is_auto_generated && (
                                        <Tooltip title={p.auto_generated_reason || 'Auto-generated'}>
                                            <Chip size="small" label="AUTO" sx={{ ml: 1, fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.65rem', bgcolor: '#e0e7ff', color: '#3730a3' }} />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>{p.vendor_name}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(p.order_date)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(p.expected_delivery_date)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                    {formatCurrency(p.total_amount, p.currency)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">
                                    <Chip size="small" label={`${Number(p.received_percentage || 0).toFixed(0)}%`}
                                        sx={{
                                            fontFamily: FONT_FAMILY, fontWeight: 600,
                                            bgcolor: p.received_percentage >= 100 ? '#dcfce7' : '#fef3c7',
                                            color: p.received_percentage >= 100 ? '#166534' : '#713f12',
                                        }} />
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={p.status?.replace('_', ' ')}
                                        style={statusChip(p.status, PO_STATUS_COLORS)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.72rem' }} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }}
                />
            </Paper>
        </Box>
    );
}
